import {
  type BrandContextState,
  type PageEnvironmentConnectionState,
  type UserCentricsConsentState,
  subscribe,
} from '@seamless/one-context';
import {
  BRAND_CONTEXT_CONNECTION_NAME,
  COMPONENT_NAME,
  CONSENT_CONNECTION_NAME,
  GLOBAL_CONTEXT_KEY,
  PAGE_CONNECTION_NAME,
  QUALTRICS_BRAND_ATTRIBUTE,
  QUALTRICS_CONSENT_NAME,
  QUALTRICS_DEFAULT_BRAND,
  QUALTRICS_DIV_ID,
  QUALTRICS_DIV_ID_NONPROD,
  QUALTRICS_SCRIPT_SRC,
  QUALTRICS_SCRIPT_SRC_NONPROD,
  TENANT,
} from './constants';

function onDomReady(callback: () => void) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
  } else {
    callback();
  }
}

function loadQualtricsOnConsent(isProd: boolean) {
  subscribe<UserCentricsConsentState[]>(CONSENT_CONNECTION_NAME, {
    tenant: TENANT,
    componentName: COMPONENT_NAME,
    callback: (state) => {
      if (state?.some((consent) => consent.name === QUALTRICS_CONSENT_NAME && consent.enabled)) {
        injectQualtricsScript(isProd);
      }
    },
  });
}

function injectQualtricsScript(isProd: boolean) {
  onDomReady(() => {
    const script = document.createElement('script');
    script.src = isProd ? QUALTRICS_SCRIPT_SRC : QUALTRICS_SCRIPT_SRC_NONPROD;
    script.async = true;
    if (document.body) {
      document.body.appendChild(script);
    }
  });
}

function resolveQualtricsParent(): Element | null {
  const main = document.querySelector('main');
  if (!main) return null;

  // The new render (SSR/nuxt) exposes window.__GLOBAL_CONTEXT__ and does not inject
  // an AEM `.aem-Grid` wrapper, so the div must be a direct child of <main>.
  if (GLOBAL_CONTEXT_KEY in window) return main;

  // Legacy AEM render injects a `.aem-Grid` wrapper as main's first child.
  const aemGrid = main.querySelector(':scope > .aem-Grid');
  return aemGrid ?? main.firstElementChild;
}

function getOrCreateDiv(id: string, parent: Element): HTMLDivElement {
  const existing = document.getElementById(id) as HTMLDivElement | null;
  if (existing) return existing;

  const div = document.createElement('div');
  div.id = id;
  parent.appendChild(div);
  return div;
}

function addQualtricsDiv(isProd: boolean) {
  const divIds = isProd ? [QUALTRICS_DIV_ID] : [QUALTRICS_DIV_ID, QUALTRICS_DIV_ID_NONPROD];

  subscribe<BrandContextState>(BRAND_CONTEXT_CONNECTION_NAME, {
    tenant: TENANT,
    componentName: COMPONENT_NAME,
    callback: (state) => {
      const brand = state.subBrand ?? QUALTRICS_DEFAULT_BRAND;
      const parent = resolveQualtricsParent();
      if (!parent) return;

      divIds.forEach((id) => {
        getOrCreateDiv(id, parent).setAttribute(QUALTRICS_BRAND_ATTRIBUTE, brand);
      });
    },
  });
}

let initialized = false;

subscribe<PageEnvironmentConnectionState>(PAGE_CONNECTION_NAME, {
  tenant: TENANT,
  componentName: COMPONENT_NAME,
  callback: (state) => {
    if (initialized) return;
    initialized = true;

    const isProd = state.pageEnvironmentVariables.stage.toLowerCase() === 'prod';
    addQualtricsDiv(isProd);
    loadQualtricsOnConsent(isProd);
  },
});
